import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../utils/formatters';

/**
 * Bank-statement tab state + fetchers + allocation dialog.
 *
 * Deliberately preserves the parent's original semantics exactly:
 *   • fetchStatement is a plain async fn (no useCallback) — matches parent
 *   • fetchLoansList uses useCallback([token]) — matches parent
 *   • allocateDialog.open → auto-load loans list, same deps as parent
 *   • handleAllocate closes over fetchStatement — matches parent
 */
export function useBankStatement({ token, headers }) {
  const [statementInvestor, setStatementInvestor] = useState('');
  const [statement, setStatement] = useState(null);
  const [statementLoading, setStatementLoading] = useState(false);
  const [allocateDialog, setAllocateDialog] = useState({ open: false, txn: null, loanId: '' });
  const [loansList, setLoansList] = useState([]);
  const [loansSearch, setLoansSearch] = useState('');
  const [selectedStmt, setSelectedStmt] = useState(new Set());

  const fetchStatement = async () => {
    if (!statementInvestor) return;
    setStatementLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/investor-transactions/bank-statement/${statementInvestor}`, { headers });
      setStatement(r.data);
    } catch {
      toast.error('Failed to load statement');
    } finally {
      setStatementLoading(false);
    }
  };

  const fetchLoansList = useCallback(async (search = '') => {
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const r = await axios.get(`${API_URL}/api/admin/investor-transactions/loans-list${params}`, { headers });
      setLoansList(r.data.loans || []);
    } catch { /* silent fail — matches parent */ }
  }, [token]);

  const handleAllocate = async () => {
    if (!allocateDialog.loanId) return;
    try {
      await axios.patch(`${API_URL}/api/admin/investor-transactions/${allocateDialog.txn.id}/allocate?loan_id=${allocateDialog.loanId}`, {}, { headers });
      toast.success('Allocated'); setAllocateDialog({ open: false, txn: null, loanId: '' });
      fetchStatement();
    } catch { toast.error('Allocation failed'); }
  };

  useEffect(() => {
    if (allocateDialog.open) fetchLoansList(loansSearch);
  }, [allocateDialog.open, loansSearch]);

  return {
    statementInvestor, setStatementInvestor,
    statement, setStatement,
    statementLoading,
    fetchStatement,
    allocateDialog, setAllocateDialog,
    loansList,
    loansSearch, setLoansSearch,
    fetchLoansList,
    selectedStmt, setSelectedStmt,
    handleAllocate,
  };
}
